// =============================================================================
// AuditLogPage — /admin/audit-logs (owner/admin, system.view_audit_log)
// -----------------------------------------------------------------------------
// A read-only window on the AuditLog trail: who did what, when, across orders,
// quotations, delivery notes, payments, cash close and remittances. Filter by
// entity type, action, user, date and free text; page server-side; expand a row
// to see the before→after change.
// =============================================================================
import { Fragment, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AUDIT_ACTION_LABEL,
  AUDIT_ENTITY_TYPES,
  auditActionLabel,
  auditActionTone,
} from '@kalachand/shared';
import { apiGet } from '../../lib/api';
import { PageScaffold } from '../../components/PageScaffold';
import { Card } from '../../components/Card';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { cn } from '../../lib/cn';

const PAGE_SIZE = 50;

interface AuditRow {
  id: string;
  at: string;
  entityType: string;
  entityId: string;
  action: string;
  beforeJson: unknown;
  afterJson: unknown;
  user: { id: string; name: string } | null;
}
interface AuditResponse {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

// Action options for the filter, sorted alphabetically by label.
const ACTION_OPTIONS = Object.keys(AUDIT_ACTION_LABEL).sort((a, b) =>
  AUDIT_ACTION_LABEL[a]!.localeCompare(AUDIT_ACTION_LABEL[b]!),
);

// Route to the affected record where we have a detail page for it.
const ENTITY_ROUTE: Record<string, string> = {
  Order: '/orders',
  Quotation: '/quotations',
  DeliveryNote: '/delivery-notes',
};
function entityLink(row: AuditRow): string | null {
  const base = ENTITY_ROUTE[row.entityType];
  return base ? `${base}/${row.entityId}` : null;
}

export function AuditLogPage(): JSX.Element {
  // Filters live in the URL so the view is shareable and survives back-navigation.
  const [searchParams, setSearchParams] = useSearchParams();
  const entityType = searchParams.get('entityType') ?? '';
  const action = searchParams.get('action') ?? '';
  const userId = searchParams.get('userId') ?? '';
  const q = searchParams.get('q') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const patch = (p: Record<string, string | null>, resetPage = true): void => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(p)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const logs = useQuery({
    queryKey: ['audit-logs', { entityType, action, userId, q, from, to, page }],
    queryFn: () =>
      apiGet<AuditResponse>('/audit-logs', {
        ...(entityType ? { entityType } : {}),
        ...(action ? { action } : {}),
        ...(userId ? { userId } : {}),
        ...(q ? { q } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        page: String(page),
        pageSize: String(PAGE_SIZE),
      }),
  });

  // Actor filter options. Needs users.manage (owner/admin — the same people who
  // hold system.view_audit_log); if a custom overlay grants only the audit perm
  // the query 403s quietly and the dropdown just stays empty.
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => apiGet<{ id: string; name: string; isActive: boolean }[]>('/users'),
    retry: false,
  });
  const users = usersQuery.data ?? [];

  const rows = logs.data?.rows ?? [];
  const total = logs.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = !!(entityType || action || userId || q || from || to);

  return (
    <PageScaffold
      scroll={false}
      title="Audit Log"
      subtitle="Who did what, across every module"
    >
      <Card className="shrink-0" bodyClassName="p-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-ink-3">
            Search
            <input
              value={q}
              onChange={(e) => patch({ q: e.target.value })}
              placeholder="record id, action, user…"
              className="kc-input w-56 mt-1 block"
            />
          </label>
          <label className="text-xs text-ink-3">
            Entity
            <select
              value={entityType}
              onChange={(e) => patch({ entityType: e.target.value })}
              className="kc-input mt-1 block"
            >
              <option value="">All</option>
              {AUDIT_ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-ink-3">
            Action
            <select
              value={action}
              onChange={(e) => patch({ action: e.target.value })}
              className="kc-input mt-1 block max-w-[16rem]"
            >
              <option value="">All</option>
              {ACTION_OPTIONS.map((a) => (
                <option key={a} value={a}>{auditActionLabel(a)}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-ink-3">
            User
            <select
              value={userId}
              onChange={(e) => patch({ userId: e.target.value })}
              className="kc-input mt-1 block max-w-[12rem]"
            >
              <option value="">All</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.isActive ? u.name : `${u.name} (inactive)`}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-ink-3">
            From
            <input type="date" value={from} onChange={(e) => patch({ from: e.target.value })} className="kc-input mt-1 block" />
          </label>
          <label className="text-xs text-ink-3">
            To
            <input type="date" value={to} onChange={(e) => patch({ to: e.target.value })} className="kc-input mt-1 block" />
          </label>
          {hasFilters && (
            <button
              type="button"
              onClick={() => patch({ entityType: null, action: null, userId: null, q: null, from: null, to: null })}
              className="text-xs text-ink-3 underline hover:text-ink pb-2"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      <DataTableCard
        footer={
          total > 0 ? (
            <Pagination
              page={Math.min(page, pageCount)}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={(p) => patch({ page: String(p) }, false)}
              unit="entry"
            />
          ) : undefined
        }
      >
        {logs.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-ink-3">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No entries" description="No audit entries match these filters." />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">User</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Entity</th>
                <th className="px-4 py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AuditRowView key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}

function AuditRowView({ row }: { row: AuditRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  const link = entityLink(row);
  const hasDetail = row.beforeJson != null || row.afterJson != null;
  return (
    <Fragment>
      <tr
        className={cn('border-b border-rule-2', hasDetail && 'cursor-pointer hover:bg-paper-2')}
        onClick={() => hasDetail && setOpen((o) => !o)}
      >
        <td className="px-4 py-2 whitespace-nowrap text-ink-2">{new Date(row.at).toLocaleString()}</td>
        <td className="px-4 py-2 whitespace-nowrap">{row.user?.name ?? <span className="text-ink-4 italic">system</span>}</td>
        <td className="px-4 py-2">
          <StatusPill tone={auditActionTone(row.action)}>{auditActionLabel(row.action)}</StatusPill>
        </td>
        <td className="px-4 py-2 whitespace-nowrap">
          <span className="text-ink-3">{row.entityType}</span>{' '}
          {link ? (
            <Link to={link} className="text-accent hover:underline kc-mono text-xs" onClick={(e: MouseEvent) => e.stopPropagation()}>
              {row.entityId.slice(-8)}
            </Link>
          ) : (
            <span className="kc-mono text-xs text-ink-4">{row.entityId.slice(-8)}</span>
          )}
        </td>
        <td className="px-4 py-2 text-ink-3 text-xs">{hasDetail ? (open ? 'Hide' : 'View') : '—'}</td>
      </tr>
      {open && hasDetail && (
        <tr className="border-b border-rule-2 bg-paper-2/50">
          <td colSpan={5} className="px-4 py-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <JsonBlock label="Before" value={row.beforeJson} />
              <JsonBlock label="After" value={row.afterJson} />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
      {value == null ? (
        <div className="text-xs text-ink-4 italic">—</div>
      ) : (
        <pre className="text-xs bg-surface border border-rule rounded p-2 overflow-x-auto kc-mono whitespace-pre-wrap">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
